import type { SyncHttpClient } from './types.js';
import type { SyncKey } from './sync-keys-shared.js';

export type { SyncKey };

export const SYNC_KEYS: SyncKey[] = [
  'products',
  'categories',
  'branchInventory',
  'salesHistory',
  'refunds',
  'branches',
];

export interface SyncPullKeyDelta {
  version: number;
  updatedIds: string[];
  deletedIds: string[];
  entities?: unknown[];
}

export interface SyncPullResponse {
  data: Partial<Record<SyncKey, SyncPullKeyDelta>>;
}

export interface SyncPushOperation {
  clientOpId: string;
  kind: 'sale' | 'refund';
  payload: Record<string, unknown>;
}

export class SyncApiClient {
  constructor(private readonly http: SyncHttpClient) {}

  async pull(
    accessToken: string,
    branchId: string,
    versions: Partial<Record<SyncKey, number>>,
    keys?: SyncKey[],
  ): Promise<SyncPullResponse> {
    const res = await this.http.postJson('/api/sync/pull', accessToken, {
      branchId,
      versions,
      ...(keys?.length ? { keys } : {}),
    });
    if (!res.ok) {
      throw new Error(`sync pull HTTP ${res.status}`);
    }
    return res.data as SyncPullResponse;
  }

  async fetchEntity(
    accessToken: string,
    key: SyncKey,
    id: string,
    branchId?: string,
  ): Promise<unknown> {
    const qs = branchId ? `?branchId=${encodeURIComponent(branchId)}` : '';
    const body = await this.http.getJson(
      `/api/sync/entities/${key}/${encodeURIComponent(id)}${qs}`,
      accessToken,
    );
    return (body as { data?: unknown }).data;
  }

  async push(
    accessToken: string,
    operations: SyncPushOperation[],
  ): Promise<{
    results: Array<{
      clientOpId: string;
      status: string;
      sale?: { visibleId: string; receiptId: string; cartId: string };
      refund?: { visibleId: string; originalReceiptId: string };
      error?: string;
    }>;
  }> {
    const res = await this.http.postJson('/api/sync/push', accessToken, { operations });
    if (!res.ok) {
      throw new Error(`sync push HTTP ${res.status}`);
    }
    const data = res.data as { data?: { results: unknown[] } };
    return (data.data ?? { results: [] }) as {
      results: Array<{
        clientOpId: string;
        status: string;
        sale?: { visibleId: string; receiptId: string; cartId: string };
        refund?: { visibleId: string; originalReceiptId: string };
        error?: string;
      }>;
    };
  }
}
